import { useMediaQuery } from '@mui/material';
import moment from 'moment-timezone';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { fetchMultiProductInstances } from 'ducks/client/productInstances';
import { ProductGroupSingleMonthDatePicker } from 'components/MultiProductDateForm/ProductGroupSingleMonthDatePicker';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ReduxState } from 'ducks';
import { getAttrByDateMap } from 'lib/util/datePickerHelpers';
import type { DateAttributes } from 'lib/util/datePickerHelpers';
import { BookingWidgetCalendarProductGroup } from 'models/settings';

interface Props {
  onDateClick: (date: string) => void;
  productGroup?: BookingWidgetCalendarProductGroup;
}

export const ProductGroupDatePicker = ({ onDateClick, productGroup }: Props) => {
  const { i18n } = useTranslation();
  const { apiKey } = React.useContext(ApiKeyContext);
  const dispatch = useDispatch();
  const locale = i18n.language;

  const loading = useSelector((state: ReduxState) => state.productInstances.loading);
  const productSummaries = useSelector((state: ReduxState) => state.server.productSummaries.all);
  const productGroupInstances = useSelector(
    (state: ReduxState) => state.productInstances.productGroup
  );
  const groupProductSummaries = productSummaries.filter((product) =>
    productGroup?.product_ids?.includes(product.id)
  );

  // Currently visible calendar month as 'YYYY-MM-DD' string
  const [month, setMonth] = React.useState<string>(moment().format('YYYY-MM-DD'));

  const [startDay, setStartDay] = React.useState<moment.Moment>(moment(month).date(1));
  const [endDay, setEndDay] = React.useState<moment.Moment>(
    moment(month).endOf('month').add(1, 'day')
  );

  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const showMonthsCount = isSmallScreen ? 1 : 2;

  React.useEffect(() => {
    const productIds = productGroup?.product_ids ?? [];

    const firstDay = moment(month).date(1);
    let lastDay = moment(month).endOf('month').add(1, 'day');

    if (showMonthsCount === 2) {
      lastDay = lastDay.endOf('month').add(1, 'day');
    }
    setStartDay(firstDay);
    setEndDay(lastDay);

    if (productIds.length !== 0) {
      dispatch(
        fetchMultiProductInstances(
          apiKey,
          productIds,
          firstDay.format('YYYY-MM-DD'),
          lastDay.format('YYYY-MM-DD'),
          locale
        )
      );
    }
  }, [apiKey, locale, month, productGroup, showMonthsCount]);

  const handleNextMonthClick = () => {
    let newMonth = moment(month).endOf('month').add(1, 'day');
    if (showMonthsCount === 2) {
      newMonth = newMonth.endOf('month').add(1, 'day');
    }
    setMonth(newMonth.format('YYYY-MM-DD'));
  };
  const handlePrevMonthClick = () => {
    let newMonth = moment(month).startOf('month').subtract(1, 'day');
    if (showMonthsCount === 2) {
      newMonth = newMonth.startOf('month').subtract(1, 'day');
    }
    setMonth(newMonth.format('YYYY-MM-DD'));
  };

  const groupAttrByDateMap = productGroupInstances
    ? productGroupInstances?.map((i) => {
        const timezone =
          groupProductSummaries.find((p) => p.id === i.product_id)?.start_timezone || '';
        return getAttrByDateMap(i.product_instances ?? [], timezone, 0);
      })
    : [];
  const attrByDateMap: {
    [date: string]: Omit<DateAttributes, 'isLimitedAvailability'>;
  } = {};
  for (const target = startDay.clone(); target.isBefore(endDay); target.add(1, 'days')) {
    const key = target.format('YYYY-MM-DD');
    attrByDateMap[key] = { status: 'closed', hasPromo: false };
    if (groupAttrByDateMap?.some((g) => g[key]?.status === 'instant')) {
      attrByDateMap[key].status = 'instant';
      if (groupAttrByDateMap?.some((g) => g[key]?.status === 'instant' && g[key]?.hasPromo)) {
        attrByDateMap[key].hasPromo = true;
      }
      continue;
    }
    if (groupAttrByDateMap?.some((g) => g[key]?.status === 'request')) {
      attrByDateMap[key].status = 'request';
      if (groupAttrByDateMap?.some((g) => g[key]?.status === 'request' && g[key]?.hasPromo)) {
        attrByDateMap[key].hasPromo = true;
      }
      continue;
    }
    if (groupAttrByDateMap?.every((g) => g[key]?.status === 'fully_booked')) {
      attrByDateMap[key].status = 'fully_booked';
      continue;
    }
  }

  return (
    <>
      <div className="widget-multi-calendar__body__main">
        <ProductGroupSingleMonthDatePicker
          month={month}
          loading={loading}
          attrByDateMap={productGroupInstances?.length != 0 ? attrByDateMap : {}}
          onPrevMonthClick={handlePrevMonthClick}
          onNextMonthClick={showMonthsCount === 1 ? handleNextMonthClick : undefined}
          onDateClick={onDateClick}
        />
        {showMonthsCount === 2 && (
          <ProductGroupSingleMonthDatePicker
            loading={loading}
            month={moment(month).endOf('month').add(1, 'day').format('YYYY-MM-DD')}
            attrByDateMap={productGroupInstances?.length != 0 ? attrByDateMap : {}}
            onNextMonthClick={handleNextMonthClick}
            onDateClick={onDateClick}
          />
        )}
      </div>
    </>
  );
};
